// File: side.js
import React, { useEffect, useState } from "react";
import { Navigation } from "react-minimal-side-navigation";
import "react-minimal-side-navigation/lib/ReactMinimalSideNavigation.css";
import api from "../../../api/api";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import FieldsStore from "../../../FieldsStore";
import { Bot } from "lucide-react";
import { aiUsageCopy } from "../../../admin/aiUsageCopy";
import { Store, Dispatcher, Constants } from "../../../flux";

// react-minimal-side-navigation renders `item.title` directly as React
// children, so passing a small fragment (label + count badge) here renders
// fine even though the library's own types declare `title: string`.
const navTitleWithCount = (label, count) => {
  if (!count) return label;
  return (
    <span className="sf-sidebar-item-title">
      <span className="sf-sidebar-item-label">{label}</span>
      <span className="sf-sidebar-item-count">{count}</span>
    </span>
  );
};

export default function Sidebar({ handleToggleSidebar }) {
  const { t, i18n } = useTranslation();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const savedPack = currentUser?.pack ?? null;

  const offer = currentUser?.offer_type ?? null;
  let role = currentUser?.role ?? null;
  let commandOption = currentUser?.has_command ?? null;
  const [data, setData] = useState([{ Fields: [] }]);
  const [allSimulations, setAllSimulations] = useState([]);
  const [fields, setFields] = useState([]);
  const [userPack, setUserPack] = useState(savedPack ?? "ultra");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(Store.getSidebarCollapsed());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onChange = () => setSidebarCollapsed(Store.getSidebarCollapsed());
    Store.addChangeListener(onChange);
    return () => Store.removeChangeListener(onChange);
  }, []);

  useEffect(() => {
    const getAllSimulations = async () => {
      try {
        await api
          .get("/simulation/get-simulations")
          .then((result) => {
            let listSimulations = result.data.simulations;
            if (result.data.type === "success") {
              setAllSimulations(listSimulations);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } catch (error) {}
    };
    const fetchData = async () => {
      const response = await api.get("/field/fields");
      const newData = await response.data;
      setData(newData.farms);
    };
    // Si FieldsStore est vide, on charge les champs depuis l'API
    if (FieldsStore.getFields().length === 0) {
      api.get("/field/fields").then((res) => {
        const newData = res.data;
        let Fields = [];
        newData.farms.forEach((item) => {
          let fields = item.fields;
          if (fields) {
            fields.forEach((itemfield) => {
              Fields.push({
                title: itemfield.name,
                status: itemfield.status,
                description: itemfield.description,
                Uid: itemfield.uid,
                farm_id: itemfield.farm_id,
                Latitude: itemfield.Latitude,
                Longitude: itemfield.Longitude,
                Id: itemfield.id,
              });
            });
          }
        });
        console.log("🌾 Fields avec next=null :", Fields);
        FieldsStore.setFields(Fields);
      });
    }
    getAllSimulations();
    fetchData();
  }, []);

  useEffect(() => {
    if (role !== "ROLE_USER" || savedPack) {
      return;
    }

    api
      .get("/profil")
      .then((response) => {
        const fetchedPack = response?.data?.result?.pack;

        if (!fetchedPack) {
          return;
        }

        setUserPack(fetchedPack);
        localStorage.setItem(
          "user",
          JSON.stringify({ ...currentUser, pack: fetchedPack }),
        );
      })
      .catch((error) => {
        console.log(error);
      });
  }, [role, savedPack]);

  // Synchronise les champs avec le singleton
  useEffect(() => {
    setFields(FieldsStore.getFields());
    const listener = (newFields) => setFields(newFields);
    FieldsStore.subscribe(listener);
    return () => FieldsStore.unsubscribe(listener);
  }, []);

  let Fields =
    Array.isArray(fields) && fields.length > 0
      ? fields.map((field) => ({
          title: field.title,
          itemId: "/Fields/" + field.Uid,
          itemUid: field.Uid,
          elemBefore: () => (
            <i className="fas fa-map-marked-alt" style={{ width: "20px" }}></i>
          ),
        }))
      : [
          {
            title: t("no_fields"), // ou "Aucun champ"
            itemId: "/Fields/none",
            disabled: true,
          },
        ];
  let Sensors = [];
  data.map((item) => {
    let fields = item.fields;
    if (fields) {
      fields.map((itemfield) => {
        let sensors = itemfield.sensors;
        if (sensors) {
          sensors.forEach((sensor) => {
            if (sensor.user_id === item.user_id) {
              Sensors.push({
                title:
                  sensor.sensorName && sensor.sensorName.trim() !== ""
                    ? sensor.sensorName
                    : sensor.code,
                itemId: "/Sensors/" + sensor.code,
                itemUid: sensor.uid,
              });
            }
          });
        }
      });
    }
  });
  let simulations = [];

  allSimulations.map((simulation) => {
    if (simulation.name) {
      simulations.push({
        title: simulation.name,
        itemId: "/Simulations/" + simulation.id,
      });
    } else {
      simulations.push({
        title: simulation.code,
        itemId: "/Simulations/" + simulation.id,
      });
    }
  });
  // 🔁 Écoute les mises à jour de capteurs
  useEffect(() => {
    const handleSensorUpdated = (e) => {
      const updated = e.detail;
      if (!updated) return;

      // On met à jour le state localement
      setData((prevData) =>
        prevData.map((farm) => ({
          ...farm,
          fields: farm.fields.map((field) => ({
            ...field,
            sensors: field.sensors.map((sensor) =>
              sensor.id === updated.id
                ? {
                    ...sensor,
                    sensorName: updated.sensorName,
                    code: updated.code,
                  }
                : sensor,
            ),
          })),
        })),
      );
    };

    window.addEventListener("sensor-updated", handleSensorUpdated);
    return () =>
      window.removeEventListener("sensor-updated", handleSensorUpdated);
  }, []);

  // const history = useHistory();

  /*const Fields = [
    {
      title: "Field 1",
      itemId: "/Fields/1"
    },
    {
      title: "Field 2",
      itemId: "/Fields/2"
    },
    {
      title: "Field 3",
      itemId: "/Fields/3"
    }
  ];*/

  const AddElementsSubNavItems = [
    {
      title: `${t("farms")}`,
      itemId: "/AddFarm",
    },
    {
      title: `${t("fields")}`,
      itemId: "/AddField",
    },
    {
      title: `${t("sensors")}`,
      itemId: "/AddSensor",
    },
    {
      title: `${t("Equipment")}`,
      itemId: "/AddEquipment",
    },
    {
      title: `${t("water_balance")}`,
      itemId: "/Bilan",
    },
  ];

  const ConfigsSubNavItems = [
    {
      title: `${t("Countries")}`,
      itemId: "/admin/configuration",
    },
    {
      title: `${t("Cities")}`,
      itemId: "/admin/configuration/cities",
    },
    {
      title: `${t("Crops")}`,
      itemId: "/admin/configuration/crops",
    },
    {
      title: `${t("Crops Varieties")}`,
      itemId: "/admin/configuration/cropsVariety",
    },
    {
      title: `${t("Soils")}`,
      itemId: "/admin/configuration/soils",
    },
    {
      title: `${t("Irrigations")}`,
      itemId: "/admin/configuration/irrigations",
    },
  ];

  const ManagementSubNavItems = [
    { title: aiUsageCopy(i18n.language).nav, itemId: "/admin/ai-usage", elemBefore: () => <Bot size={18} aria-hidden="true" /> },
    {
      title: `${t("users_manag")}`,
      itemId: "/admin/users",
      elemBefore: () => (
        <i className="fas fa-edit" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: `${t("sensors_manag")}`,
      itemId: "/admin/sensors",
      elemBefore: () => (
        <i className="fas fa-edit" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: `${t("fields_manag")}`,
      itemId: "/admin/fields",
      elemBefore: () => (
        <i className="fas fa-edit" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: `${t("commande_manag")}`,
      itemId: "/admin/commande",
      elemBefore: () => (
        <i className="fas fa-edit" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: `${t("satellite_manag")}`,
      itemId: "/admin/satellite-images",
      elemBefore: () => (
        <i className="fas fa-edit" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: `${t("Suivi_irrigation")}`,
      itemId: "/admin/Suivi_irrigation",
      elemBefore: () => (
        <i className="fas fa-edit" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: `${t("support_center_title")}`,
      itemId: "/admin/support-center",
      elemBefore: () => (
        <i className="fas fa-headset" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: `${t("Sensor Installation Manager")}`,
      itemId: "/admin/configuration/SensorInstallationManager",
      elemBefore: () => (
        <i className="fas fa-edit" style={{ width: "20px" }}></i>
      ),
    },
  ];

  const AddElementsFreeSubNavItems = [
    {
      title: `${t("water_balance")}`,
      itemId: "/Bilan",
      elemBefore: () => (
        <i
          className="fas fa-droplet"
          style={{ width: "20px" }}
          aria-hidden="true"
        />
      ),
    },
  ];
  const isSubUser = !!currentUser?.head;

  const adminOverviewItem = {
    title: `${t("admin_overview_sidebar")}`,
    itemId: "/admin/overview",
    elemBefore: () => (
      <i className="fas fa-chart-line" style={{ width: "20px" }}></i>
    ),
  };

  const insertAdminOverviewItem = () => {
    if (role !== "ROLE_ADMIN" || isSubUser) {
      return;
    }

    if (Items.some((item) => item.itemId === adminOverviewItem.itemId)) {
      return;
    }

    const dashboardIndex = Items.findIndex(
      (item) => item.itemId === "/" || item.itemId === "/Dashboard",
    );

    if (dashboardIndex >= 0) {
      Items.splice(dashboardIndex + 1, 0, adminOverviewItem);
    } else {
      Items.unshift(adminOverviewItem);
    }
  };

  let Items = [
    {
      title: `${t("dashboard")}`,
      itemId: "/",
      elemBefore: () => (
        <i className="fas fa-desktop" style={{ width: "20px" }}></i>
      ),
    },
    {
      title: navTitleWithCount(t("fields"), fields.length),
      itemId: "/MFields",
      elemBefore: () => (
        <i className="fas fa-tractor" style={{ width: "20px" }}></i>
      ),
      subNav: Fields,
    },
  ];

  // ⚡ Sub-users menu
  if (isSubUser) {
    Items.push(
      {
        title: `${t("weather_forecast")}`,
        itemId: "/Weather",
        elemBefore: () => (
          <i className="fas fa-cloud" style={{ width: "20px" }}></i>
        ),
      },
      {
        title: `${t("calendar")}`,
        itemId: "/Calendar",
        elemBefore: () => (
          <i className="fas fa-calendar-week" style={{ width: "20px" }}></i>
        ),
      },
      {
        title: `${t("my_simulations")}`,
        itemId: "/MSimulations/:idSimulation",
        elemBefore: () => (
          <i
            className="fas fa-flask"
            style={{ width: "20px" }}
            aria-hidden="true"
          />
        ),
        subNav: simulations,
      },
    );
  } else {
    // ⚡ Normal/head user full menu in specific order
    Items.push(
      {
        title: navTitleWithCount(t("sensors"), Sensors.length),
        itemId: "/MSensors",
        elemBefore: () => (
          <i className="fas fa-satellite-dish" style={{ width: "20px" }}></i>
        ),
        subNav: Sensors,
      },
      {
        title: `${t("weather_forecast")}`,
        itemId: "/Weather",
        elemBefore: () => (
          <i className="fas fa-cloud" style={{ width: "20px" }}></i>
        ),
      },
      {
        title: `${t("Climate_History")}`,
        itemId: "/climatehistory",
        elemBefore: () => (
          <i className="fas fa-cloud-sun" style={{ width: "20px" }}></i>
        ),
      },
      {
        title: `${t("Satellite Images")}`,
        itemId: "/satellite-images",
        elemBefore: () => (
          <i className="fas fa-satellite" style={{ width: "20px" }}></i>
        ),
      },
      {
        title: `${t("calendar")}`,
        itemId: "/Calendar",
        elemBefore: () => (
          <i className="fas fa-calendar-week" style={{ width: "20px" }}></i>
        ),
      },
      {
        title: navTitleWithCount(t("my_simulations"), simulations.length),
        itemId: "/MSimulations/:idSimulation",
        elemBefore: () => (
          <i
            className="fas fa-flask"
            style={{ width: "20px" }}
            aria-hidden="true"
          />
        ),
        subNav: simulations,
      },
      {
        title: `${t("fields_settings")}`,
        itemId: "/AddField",
        elemBefore: () => (
          <i className="fa fa-cog" style={{ width: "20px" }}></i>
        ),
      },
      {
        title: `${t("sensor_settings")}`,
        itemId: "/sensor-settings",
        elemBefore: () => (
          <i className="fa fa-cog" style={{ width: "20px" }}></i>
        ),
      },
    );
  }

  if (!isSubUser) {
    Items.push({
      title: `${t("sub-users-management")}`,
      itemId: "/sub-users-management",
      elemBefore: () => <i className="fa fa-cog" style={{ width: "20px" }}></i>,
    });
  }
  const addToSidebar = () => {
    // let role = JSON.parse(localStorage.getItem("user")).role;
    let role = JSON.parse(localStorage.getItem("user"))?.role ?? null;

    if (role === "ROLE_ADMIN") {
      return Items.push(
        {
          title: `${t("Configuration")}`,
          itemId: "/Madmin/configuration",
          elemBefore: () => (
            <i className="fas fa-edit" style={{ width: "20px" }}></i>
          ),
          subNav: ConfigsSubNavItems,
        },
        {
          title: `${t("Management")}`,
          itemId: "/Madmin/Management",
          elemBefore: () => (
            <i className="fas fa-edit" style={{ width: "20px" }}></i>
          ),
          subNav: ManagementSubNavItems,
        },
        // {
        //   title: `${t('collect_weather_data')}`,
        //   itemId: "/admin/collect-data/weather",
        //   elemBefore: () => <i className="fas fa-align-justify" style={{ width: "20px" }}></i>
        // }
      );
    }
  };

  addToSidebar();
  const displayByOffer = () => {
    // The free-offer navigation applies only to farm users. Administrators
    // must keep their management navigation regardless of their offer type.
    if (role === "ROLE_USER" && offer === "1") {
      return (Items = [
        {
          title: `${t("dashboard")}`,
          itemId: "/Dashboard",
          elemBefore: () => (
            <i className="fas fa-desktop" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("add_element")}`,
          itemId: "/MAdd",
          elemBefore: () => (
            <i className="fas fa-plus-circle" style={{ width: "20px" }}></i>
          ),
          subNav: AddElementsFreeSubNavItems,
        },
        {
          title: `${t("my_simulations")}`,
          itemId: "/MSimulations",
          elemBefore: () => (
            <i
              className="fas fa-flask"
              style={{ width: "20px" }}
              aria-hidden="true"
            />
          ),
          subNav: simulations,
        },
        {
          title: `${t("calendar")}`,
          itemId: "/Calendar",
          elemBefore: () => (
            <i className="fas fa-calendar-week" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("weather_forecast")}`,
          itemId: "/Weather",
          elemBefore: () => (
            <i className="fas fa-cloud" style={{ width: "20px" }}></i>
          ),
        },
      ]);
    }
  };

  const displayByRole = () => {
    if (role === "ROLE_SUPPLIER") {
      return (Items = [
        {
          title: `${t("company_dashboard")}`,
          itemId: "/Dashboard-supplier",
          elemBefore: () => (
            <i className="fas fa-desktop" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("my_dashboard")}`,
          itemId: "/",
          elemBefore: () => (
            <i className="fas fa-desktop" style={{ width: "20px" }}></i>
          ),
        },
        // {
        //   title: `${t('add_element')}`,
        //   itemId: "/MAdd",
        //   elemBefore: () => (
        //     <i className="fas fa-plus-circle" style={{ width: "20px" }}></i>
        //   ),
        //   subNav: AddElementsSubNavItems
        // },
        {
          title: `${t("fields")}`,
          itemId: "/MFields",
          elemBefore: () => (
            <i className="fas fa-tractor" style={{ width: "20px" }}></i>
          ),
          subNav: Fields,
        },
        {
          title: `${t("sensors")}`,
          itemId: "/Sensors",
          elemBefore: () => (
            <i className="fas fa-satellite-dish" style={{ width: "20px" }}></i>
          ),
        },
        // {
        //   title: `${t('Commands')}`,
        //   itemId: "/Commands",
        //   elemBefore: () => (
        //     <i className="material-icons" style={{ width: "20px" }}>&#xe429;</i>
        //   )
        // },
        {
          title: `${t("my_simulations")}`,
          itemId: "/MSimulations",
          elemBefore: () => (
            <i
              className="fas fa-flask"
              style={{ width: "20px" }}
              aria-hidden="true"
            />
          ),
          subNav: simulations,
        },
        {
          title: `${t("calendar")}`,
          itemId: "/Calendar",
          elemBefore: () => (
            <i className="fas fa-calendar-week" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("weather_forecast")}`,
          itemId: "/Weather",
          elemBefore: () => (
            <i className="fas fa-cloud" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("users_manag")}`,
          itemId: "/supplier/users",
          elemBefore: () => (
            <i className="fas fa-edit" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("sensors_manag")}`,
          itemId: "/supplier/sensors",
          elemBefore: () => (
            <i className="fas fa-edit" style={{ width: "20px" }}></i>
          ),
        },
      ]);
    }
  };

  const displayByCommand = () => {
    if (commandOption === "1" && role === "ROLE_USER") {
      return (Items = [
        {
          title: `${t("dashboard")}`,
          itemId: "/",
          elemBefore: () => (
            <i className="fas fa-desktop" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("fields")}`,
          itemId: "/MFields",
          elemBefore: () => (
            <i className="fas fa-tractor" style={{ width: "20px" }}></i>
          ),
          subNav: Fields,
        },
        {
          title: `${t("sensors")}`,
          itemId: "/Sensors",
          elemBefore: () => (
            <i className="fas fa-satellite-dish" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("my_simulations")}`,
          itemId: "/MSimulations",
          elemBefore: () => (
            <i
              className="fas fa-flask"
              style={{ width: "20px" }}
              aria-hidden="true"
            />
          ),
          subNav: simulations,
        },
        {
          title: `${t("fields_settings")}`,
          itemId: "/AddField",
          elemBefore: () => (
            <i className="fa fa-cog" style={{ width: "20px" }}></i>
          ),
        },
        // Pas de route "/Commands" déclarée dans protectedRoutes.js : ce lien
        // menait à une 404. Retiré tant qu'aucune route réelle n'existe.
        // {
        //   title: `${t("Commands")}`,
        //   itemId: "/Commands",
        //   elemBefore: () => (
        //     <i
        //       className="fas fa-terminal"
        //       style={{ width: "20px" }}
        //       aria-hidden="true"
        //     />
        //   ),
        // },
        {
          title: `${t("calendar")}`,
          itemId: "/Calendar",
          elemBefore: () => (
            <i className="fas fa-calendar-week" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("weather_forecast")}`,
          itemId: "/Weather",
          elemBefore: () => (
            <i className="fas fa-cloud" style={{ width: "20px" }}></i>
          ),
        },
      ]);
    }
    if (commandOption === "1" && role === "ROLE_ADMIN") {
      return (Items = [
        {
          title: `${t("dashboard")}`,
          itemId: "/",
          elemBefore: () => (
            <i className="fas fa-desktop" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("fields")}`,
          itemId: "/MFields",
          elemBefore: () => (
            <i className="fas fa-tractor" style={{ width: "20px" }}></i>
          ),
          subNav: Fields,
        },
        {
          title: `${t("sensors")}`,
          itemId: "/Sensors",
          elemBefore: () => (
            <i className="fas fa-satellite-dish" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("my_simulations")}`,
          itemId: "/MSimulations",
          elemBefore: () => (
            <i
              className="fas fa-flask"
              style={{ width: "20px" }}
              aria-hidden="true"
            />
          ),
          subNav: simulations,
        },
        {
          title: `${t("fields_settings")}`,
          itemId: "/AddField",
          elemBefore: () => (
            <i className="fa fa-cog" style={{ width: "20px" }}></i>
          ),
        },
        // Pas de route "/Commands" déclarée dans protectedRoutes.js : ce lien
        // menait à une 404. Retiré tant qu'aucune route réelle n'existe.
        // {
        //   title: `${t("Commands")}`,
        //   itemId: "/Commands",
        //   elemBefore: () => (
        //     <i
        //       className="fas fa-terminal"
        //       style={{ width: "20px" }}
        //       aria-hidden="true"
        //     />
        //   ),
        // },
        {
          title: `${t("calendar")}`,
          itemId: "/Calendar",
          elemBefore: () => (
            <i className="fas fa-calendar-week" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("weather_forecast")}`,
          itemId: "/Weather",
          elemBefore: () => (
            <i className="fas fa-cloud" style={{ width: "20px" }}></i>
          ),
        },
        {
          title: `${t("Configuration")}`,
          itemId: "/Madmin/configuration",
          elemBefore: () => (
            <i className="fas fa-edit" style={{ width: "20px" }}></i>
          ),
          subNav: ConfigsSubNavItems,
        },
        {
          title: `${t("Management")}`,
          itemId: "/Madmin/Management",
          elemBefore: () => (
            <i className="fas fa-edit" style={{ width: "20px" }}></i>
          ),
          subNav: ManagementSubNavItems,
        },
        // {
        //   title: `${t('collect_weather_data')}`,
        //   itemId: "/admin/collect-data/weather",
        //   elemBefore: () => <i className="fas fa-align-justify" style={{ width: "20px" }}></i>
        // }
      ]);
    }
  };
  displayByOffer();

  displayByRole();
  displayByCommand();
  insertAdminOverviewItem();

  if (role === "ROLE_USER") {
    Items = Items.filter((item) => {
      if (userPack === "basic") {
        return (
          item.itemId !== "/Weather" &&
          item.itemId !== "/satellite-images" &&
          item.itemId !== "/climatehistory"
        );
      }

      if (userPack === "pro") {
        return item.itemId !== "/satellite-images";
      }

      return true;
    });
  }

  // Inserted here, after every branch that REASSIGNS `Items` (displayByOffer,
  // displayByRole, displayByCommand) and after the pack filter, so the
  // assistant is present for all roles and is never stripped for basic/pro.
  if (!Items.some((item) => item.itemId === "/assistant")) {
    Items.splice(1, 0, {
      title: `${t("ai_assistant")}`,
      itemId: "/assistant",
      elemBefore: () => (
        <i className="fas fa-robot" style={{ width: "20px" }}></i>
      ),
    });
  }

  return (
    <div className="smartfarm-sidebar-navigation">
      <Navigation
        // you can use your own router's api to get pathname
        activeItemId={location.pathname}
        onSelect={({ itemId }) => {
          // When the sidebar is collapsed to icons-only, a parent item with
          // its own subNav (Fields, Sensors, simulations, ...) has nowhere
          // to show that subNav. Re-expand instead of navigating nowhere.
          const clickedItem = Items.find((item) => item.itemId === itemId);
          if (sidebarCollapsed && clickedItem && clickedItem.subNav) {
            Dispatcher.dispatch({
              actionType: Constants.TOGGLE_SIDEBAR_COLLAPSE
            });
            return;
          }
          // maybe push to the route
          if (itemId[1] != "M") {
            if (itemId != "/AddSensor") {
              let uid = "0";
              let id = "0";

              // Champs: /Fields/:uid -> on lit l'uid directement
              if (itemId.startsWith("/Fields/")) {
                const parts = itemId.split("/");
                const uidFromPath = parts[2] || "0";
                uid = uidFromPath;
                localStorage.setItem("Field", uidFromPath);
              } else {
                localStorage.setItem("Field", "0");
              }
              if (itemId[1] == "S") {
                if (
                  simulations[
                    simulations.findIndex((item) => item.itemId === itemId)
                  ]
                ) {
                  id =
                    simulations[
                      simulations.findIndex((item) => item.itemId === itemId)
                    ].itemId;
                }
                localStorage.setItem("Simulation", id);
              } else {
                localStorage.setItem("Simulation", 0);
              }
              if (itemId[2] == "S") {
                if (
                  Sensors[Sensors.findIndex((item) => item.itemId === itemId)]
                ) {
                  id =
                    Sensors[Sensors.findIndex((item) => item.itemId === itemId)]
                      .itemId;
                }
                localStorage.setItem("Sensors", id);
              } else {
                localStorage.setItem("Sensors", 0);
              }
              navigate(itemId, { state: uid });
              // window.location.reload()
              handleToggleSidebar();
            } else {
              navigate(itemId);
              // window.location.reload();
              handleToggleSidebar();
            }
          }
        }}
        items={Items}
      />
    </div>
  );
}
